import { Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { placeholders } from "../../../../../constants/placeholders"
import {
  formatTaxNumber,
  validateTaxNumber
} from "../../../../../utils/validation/tax-number.ts"
import { type SettingsCompanyFormData } from "../SettingsCompanyForm.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

interface CompanyLicensingFormSectionProps {
  form: FormInstance<SettingsCompanyFormData>
}

/**
 * Tax ID and licensing/insurance identifiers. These are genuinely
 * per-branch columns on `CompanyDetails` (`apps/user/models.py`, one row
 * per Team, no shared legal-entity record behind them) — confirmed by
 * reading the model rather than assumed, which is why this section is
 * NOT labelled as following a shared legal entity: there is no such
 * shared record to follow yet. A real per-branch "legal entity" concept
 * (shared tax ID/letterhead across branches operating under one license)
 * is a named, pre-existing product gap, not something this form can
 * honestly represent today.
 */
export function CompanyLicensingFormSection({ form }: CompanyLicensingFormSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
      <Form.Item<SettingsCompanyFormData>
        label={"Tax ID / VAT"}
        name="ein"
        rules={[{ validator: validateTaxNumber }]}
      >
        <TextInput
          disableAutoCapitalize
          placeholder={placeholders.taxNumber}
          maxLength={10}
          onChange={(e) => form.setFieldValue("ein", formatTaxNumber(e.target.value))}
          onPaste={(e) => {
            e.preventDefault()
            form.setFieldValue("ein", formatTaxNumber(e.clipboardData.getData("text")))
          }}
        />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"License Number"}
        name="license_number"
        rules={[
          { required: true, message: "Please enter your license number" },
          {
            pattern: /^\d{1,7}$/,
            message: "License number must be up to 7 digits"
          }
        ]}
      >
        <TextInput disableAutoCapitalize placeholder="Enter license number" maxLength={7} />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData> label={"Bond Number"} name="bond_number">
        <TextInput disableAutoCapitalize placeholder="Enter bond number" />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"General Liability"}
        name="general_liability"
      >
        <TextInput disableAutoCapitalize placeholder="Enter general liability number" />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"Insurance Number"}
        name="insurance_number"
      >
        <TextInput disableAutoCapitalize placeholder="Enter insurance number" />
      </Form.Item>
    </div>
  )
}
